import { useState, useEffect } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useSessionTimeout } from '@/hooks/useSessionTimeout'
import { Sidebar } from './Sidebar'
import { Header } from './Header'
import { Loader2 } from 'lucide-react'

const AUTH_TIMEOUT_MS = 10000 // 10 second timeout for auth loading

// Viewers may only access these paths.
// URL path stays /tokens even though the UI label says "Coins".
// Keep this in sync with the no-minRole entries in Sidebar.tsx navigation.
const VIEWER_ALLOWED_PATHS = ['/dashboard', '/tokens', '/settings', '/infrastructure', '/global-metrics']

export function DashboardLayout() {
  const { user, adminUser, isLoading, isMfaRequired } = useAuth()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [authTimedOut, setAuthTimedOut] = useState(false)

  useSessionTimeout(adminUser?.role)

  // Safety timeout - if auth is still loading after timeout, redirect to login
  useEffect(() => {
    if (!isLoading) {
      setAuthTimedOut(false)
      return
    }

    const timer = setTimeout(() => {
      if (isLoading) {
        console.warn('Auth loading timed out, redirecting to login')
        setAuthTimedOut(true)
      }
    }, AUTH_TIMEOUT_MS)

    return () => clearTimeout(timer)
  }, [isLoading])

  if (isLoading && !authTimedOut) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-brand-fg" />
      </div>
    )
  }

  if (!user || authTimedOut) {
    return <Navigate to="/login" replace />
  }

  if (isMfaRequired) {
    return <Navigate to="/mfa-verify" replace />
  }

  // Investor users go to their own layout
  if (adminUser?.role === 'investor') {
    return <Navigate to="/investor" replace />
  }

  // Viewer route guard: redirect viewers away from restricted pages
  if (adminUser?.role === 'viewer') {
    const isAllowed = VIEWER_ALLOWED_PATHS.some(
      (p) => location.pathname === p || location.pathname.startsWith(p + '/')
    )
    if (!isAllowed) {
      return <Navigate to="/dashboard" replace />
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="lg:pl-64">
        <Header onMenuClick={() => setSidebarOpen(true)} />
        <main className="p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
